// =====================================================================
// Faz 14b (2026-04-30) — Sepeti Terk Hatirlatma raporu
// =====================================================================
// Admin paneli sayfa: backend'den gelen istatistikleri ve son 10 hatirlatma
// kaydini gosterir. Recovery rate (geri kazanım oranı) hesaplanir.
//
// Endpoint:
//  - GET  /api/admin/abandoned-cart/stats     -> overall + recent[]
//  - POST /api/admin/abandoned-cart/run-now   -> manuel tick (debug)
//
// KPI'lar: 30 gunluk hatirlatma sayisi, geri kazanim sayisi/yuzdesi,
// son 24 saatte gonderilen, 7 gunde geri kazanilan, hatirlatilan toplam TL.
// =====================================================================
import React, { useEffect, useState } from "react";
import {
  Box, Card, Typography, Grid, Chip, Table, TableHead, TableRow, TableCell, TableBody,
  CircularProgress, Alert, Button, IconButton, Tooltip
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import ShoppingCartCheckoutIcon from "@mui/icons-material/ShoppingCartCheckout";
import EmailIcon from "@mui/icons-material/Email";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import API from "../services/api";
import toast from "react-hot-toast";

const fmtMoney = (v) =>
  Number(v || 0).toLocaleString("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + " ₺";
const fmtDateTime = (s) => s ? new Date(s).toLocaleString("tr-TR", {
  day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit"
}) : "—";

// KPI mini-kart
const StatCard = ({ title, value, sub, icon, color }) => (
  <Card sx={{ p: 2.2, borderRadius: 3, height: "100%", display: "flex", alignItems: "center", gap: 1.5 }}>
    <Box sx={{ bgcolor: color, color: "white", borderRadius: 2, p: 1.2, display: "flex" }}>{icon}</Box>
    <Box sx={{ flex: 1, minWidth: 0 }}>
      <Typography variant="h5" fontWeight={800} lineHeight={1.1} noWrap>{value}</Typography>
      <Typography variant="body2" color="text.secondary" mt={0.4}>{title}</Typography>
      {sub && <Typography variant="caption" color="text.disabled">{sub}</Typography>}
    </Box>
  </Card>
);

export default function AbandonedCart() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [err, setErr] = useState(null);

  const load = () => {
    setLoading(true);
    API.get("/api/admin/abandoned-cart/stats")
      .then(r => { setData(r.data); setErr(null); })
      .catch(e => setErr(e.response?.data?.error || e.message))
      .finally(() => setLoading(false));
  };

  // Manuel tick — admin debug icin (cron zaten saatte 1 calisir)
  const runNow = async () => {
    if (!window.confirm("Şimdi manuel tetiklemek istediğinize emin misiniz?\n\n24+ saat hareketsiz sepetlere mail gönderilecek.")) return;
    setRunning(true);
    try {
      const r = await API.post("/api/admin/abandoned-cart/run-now");
      const res = r.data.result || {};
      toast.success(`Tick tamamlandı — gönderilen: ${res.sent || 0}, atlananı: ${res.failed || 0}`);
      load();
    } catch (e) {
      toast.error("Hata: " + (e.response?.data?.error || e.message));
    } finally {
      setRunning(false);
    }
  };

  useEffect(() => { load(); }, []);

  if (loading && !data) {
    return <Box display="flex" justifyContent="center" mt={10}><CircularProgress /></Box>;
  }
  if (err && !data) {
    return <Alert severity="error" sx={{ mt: 4 }}>Veri yüklenemedi: {err}</Alert>;
  }

  const o = data?.overall || {};
  const recent = data?.recent || [];

  return (
    <Box>
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={3} flexWrap="wrap" gap={1}>
        <Box>
          <Typography variant="h5" fontWeight={800}>💾 Sepeti Terk Hatırlatması</Typography>
          <Typography variant="body2" color="text.secondary">
            24 saat hareketsiz sepete sahip kullanıcılara otomatik email — son 30 gün raporu
          </Typography>
        </Box>
        <Box display="flex" gap={1}>
          <Tooltip title="Şimdi manuel tetikle (cron zaten saatte 1 çalışır)">
            <span>
              <Button
                variant="outlined"
                startIcon={running ? <CircularProgress size={16} /> : <PlayArrowIcon />}
                onClick={runNow}
                disabled={running}
                size="small"
              >
                Şimdi Çalıştır
              </Button>
            </span>
          </Tooltip>
          <Tooltip title="Yenile">
            <IconButton onClick={load} size="small"><RefreshIcon /></IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* KPI Kartlar */}
      <Grid container spacing={2} mb={3}>
        <Grid item xs={12} sm={6} md={2.4}>
          <StatCard
            title="Hatırlatma (30g)"
            value={(o.sent_30d || 0).toLocaleString("tr-TR")}
            sub={`Son 24sa: ${o.sent_24h || 0}`}
            icon={<EmailIcon />}
            color="#1565C0"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <StatCard
            title="Geri Kazanım (30g)"
            value={(o.recovered_30d || 0).toLocaleString("tr-TR")}
            sub={`Son 7g: ${o.recovered_7d || 0}`}
            icon={<ShoppingCartCheckoutIcon />}
            color="#10B981"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <StatCard
            title="Geri Kazanım Oranı"
            value={`%${o.recovery_rate_pct || 0}`}
            sub={o.sent_30d > 0 ? `${o.recovered_30d}/${o.sent_30d} sipariş` : "Henüz hatırlatma yok"}
            icon={<TrendingUpIcon />}
            color="#8B5CF6"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <StatCard
            title="Hatırlatılan Tutar"
            value={fmtMoney(o.total_value_30d)}
            sub="30 günlük toplam"
            icon={<AttachMoneyIcon />}
            color="#E67E22"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <StatCard
            title="Geri Kazanılan Tutar"
            value={fmtMoney(o.recovered_value_30d)}
            sub={o.total_value_30d > 0
              ? `%${Math.round((o.recovered_value_30d / o.total_value_30d) * 100)} dönüş`
              : "Henüz dönüşüm yok"}
            icon={<AttachMoneyIcon />}
            color="#16A34A"
          />
        </Grid>
      </Grid>

      {/* Recent table */}
      <Card sx={{ borderRadius: 3, overflow: "hidden" }}>
        <Box sx={{ p: 2, borderBottom: "1px solid #eee" }}>
          <Typography variant="subtitle1" fontWeight={700}>Son 10 Hatırlatma</Typography>
        </Box>
        {recent.length === 0 ? (
          <Box p={6} textAlign="center" color="text.secondary">
            <Typography>Henüz gönderilmiş hatırlatma yok.</Typography>
            <Typography variant="caption">Cron her saat çalışır — 24+ saat hareketsiz sepete sahip ilk kullanıcı geldiğinde otomatik gönderilir.</Typography>
          </Box>
        ) : (
          <Box sx={{ overflowX: "auto" }}>
            <Table size="small">
              <TableHead sx={{ bgcolor: "#f8f9fa" }}>
                <TableRow>
                  <TableCell width={150}>Hatırlatma Zamanı</TableCell>
                  <TableCell>Müşteri</TableCell>
                  <TableCell width={100} align="center">Ürün Sayısı</TableCell>
                  <TableCell width={140} align="right">Sepet Tutarı</TableCell>
                  <TableCell width={130} align="center">Durum</TableCell>
                  <TableCell width={130}>Sipariş</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {recent.map((r) => (
                  <TableRow key={r.id} hover>
                    <TableCell sx={{ fontSize: 12 }}>{fmtDateTime(r.reminded_at)}</TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight={600}>{r.name || r.company || "—"}</Typography>
                      <Typography variant="caption" color="text.disabled">{r.email}</Typography>
                    </TableCell>
                    <TableCell align="center" sx={{ fontWeight: 600 }}>{r.item_count}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700, color: "#1a3d6d" }}>{fmtMoney(r.total_value)}</TableCell>
                    <TableCell align="center">
                      {r.recovered ? (
                        <Chip size="small" label="✅ Geri Kazanıldı" color="success" />
                      ) : (
                        <Chip size="small" label="⏳ Bekliyor" variant="outlined" />
                      )}
                    </TableCell>
                    <TableCell sx={{ fontSize: 12 }}>
                      {r.recovered_order_id ? (
                        <Box>
                          <Typography variant="body2" fontWeight={600}>#{r.recovered_order_id}</Typography>
                          <Typography variant="caption" color="text.disabled">{fmtDateTime(r.recovered_at)}</Typography>
                        </Box>
                      ) : "—"}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        )}
      </Card>
    </Box>
  );
}
